import React, {useEffect, useState} from 'react';
import {Button, Dropdown, Menu} from 'antd';
import {useLocation, useNavigate} from 'react-router-dom';
import {BookOutlined, HomeOutlined, InfoCircleOutlined, MenuOutlined, PictureOutlined, ToolOutlined, EllipsisOutlined, ThunderboltOutlined, LockOutlined, SearchOutlined} from '@ant-design/icons';
import {ROUTE_TRANSLATION_KEYS, ROUTES} from '../../routes/constants';
import { useTranslation } from '../../hooks/useTranslation';
import { isEnglishSite } from '../../config/site';
import './Navigation.css';

const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [enableImageSearch, setEnableImageSearch] = useState<boolean>(false);

  // 外部链接配置
  const externalLinks : {key: string, icon: React.ReactElement, label: string; url: string;}[]= [
    {
      key: 'external-docs',
      icon: <BookOutlined />,
      label: t('navigation.blog'),
      url: 'https://blog.imgbed.cn/',
    },
    // {
    //   key: 'external-docs',
    //   icon: <PictureOutlined />,
    //   label: '使用指南',
    //   url: 'https://www.yuque.com/aowufudongdong/kb/doa7yc25suqyq5y2',
    // },
    // {
    //   key: 'external-config',
    //   icon: <SettingOutlined />,
    //   label: '配置文档',
    //   url: 'https://www.yuque.com/aowufudongdong/kb/nlg8gih5hrv2tv3c',
    // },
    // {
    //   key: 'external-faq',
    //   icon: <QuestionCircleOutlined />,
    //   label: '常见问题',
    //   url: 'https://www.yuque.com/aowufudongdong/kb/og9axmho8yc8w71c',
    // },
  ];

  // 内部路由菜单项
  const internalMenuItems = [
    {
      key: ROUTES.HOME,
      icon: <HomeOutlined />,
      label: t(ROUTE_TRANSLATION_KEYS[ROUTES.HOME]),
    },
    {
      key: ROUTES.GALLERIES,
      icon: <PictureOutlined />,
      label: t(ROUTE_TRANSLATION_KEYS[ROUTES.GALLERIES]),
    },
    {
      key: ROUTES.IMAGE_PROCESSING,
      icon: <ToolOutlined />,
      label: t(ROUTE_TRANSLATION_KEYS[ROUTES.IMAGE_PROCESSING]),
    },
  ];

  // 获取用户配置
  useEffect(() => {
    fetch('/login/info', {
      method: 'GET',
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((e) => {
        if (e.isSuccess && e.detail) {
          try {
            const config = typeof e.detail.config === 'string' 
              ? JSON.parse(e.detail.config || '{}') 
              : e.detail.config || {};
            setEnableImageSearch(config.enableImageSearch === true);
          } catch (err) {
            console.error('解析用户配置失败:', err);
            setEnableImageSearch(false);
          }
        }
      })
      .catch((err) => {
        console.error('获取用户信息失败:', err);
        setEnableImageSearch(false);
      });
  }, []);

  // 更多菜单项（博客、关于）- 英文站不显示博客
  const moreMenuItems = [
    // 只有启用图搜功能的用户才能看到图搜入口
    ...(enableImageSearch ? [{
      key: ROUTES.IMAGE_SEARCH,
      icon: <SearchOutlined />,
      label: t(ROUTE_TRANSLATION_KEYS[ROUTES.IMAGE_SEARCH]),
    }] : []),
    // 英文站隐藏 AI 生图入口
    ...(!isEnglishSite() ? [{
      key: ROUTES.AI_GENERATE,
      icon: <ThunderboltOutlined />,
      label: t(ROUTE_TRANSLATION_KEYS[ROUTES.AI_GENERATE]),
    }] : []),
    {
      key: ROUTES.ENCRYPTED_LINKS,
      icon: <LockOutlined />,
      label: t(ROUTE_TRANSLATION_KEYS[ROUTES.ENCRYPTED_LINKS]),
    },
    // 英文站不显示博客入口
    ...(!isEnglishSite() ? [{
      key: 'external-docs',
      icon: <BookOutlined />,
      label: t('navigation.blog'),
    }] : []),
    // 英文站不展示 About 菜单
    ...(!isEnglishSite() ? [{
      key: ROUTES.ABOUT,
      icon: <InfoCircleOutlined />,
      label: t(ROUTE_TRANSLATION_KEYS[ROUTES.ABOUT]),
    }] : []),
  ];

  // 合并所有菜单项
  const menuItems = [
    ...internalMenuItems,
    {
      key: 'more',
      icon: <EllipsisOutlined />,
      label: t('navigation.more'),
      children: moreMenuItems,
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    // 处理外部博客链接
    if (key === 'external-docs') {
      window.open('https://blog.imgbed.cn/', '_blank', 'noopener,noreferrer');
      return;
    }
    // 内部路由导航
    navigate(key);
  };

  return (
    <>
      {/* 桌面端导航菜单 */}
      <div className="desktop-navigation">
        <Menu
          mode="horizontal"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{
            border: 'none',
            background: 'transparent',
            flex: 1,
            marginLeft: '0px',
          }}
        />
      </div>

      {/* 移动端下拉菜单 */}
      <div className="mobile-navigation">
        <Dropdown
          menu={{
            items: menuItems,
            selectedKeys: [location.pathname],
            onClick: handleMenuClick,
          }}
          placement="bottomRight"
          trigger={['click']}
        >
          <Button
            type="text"
            icon={<MenuOutlined />}
            style={{
              fontSize: '18px',
              color: '#4C5362',
              border: 'none',
              padding: '8px',
              height: '40px',
              width: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        </Dropdown>
      </div>
    </>
  );
};

export default Navigation; 